"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NexusModelRenderer = void 0;
const priority_queue_1 = require("./priority-queue");
const matrix_1 = require("./matrix");
const debug_1 = require("../env/debug");
const objects_1 = require("../objects");
class NodeTraversalState {
    constructor(node, visited = false, blocked = false, selected = false) {
        this.node = node;
        this.visited = visited;
        this.blocked = blocked;
        this.selected = selected;
        this.children = [];
    }
    isChildSelected(childId) {
        const child = this.children.find(child => child.node.id === childId);
        return child ? child.selected : false;
    }
    get selectedNodes() {
        const unique = new Map();
        this.addAllSelectedChildren(unique);
        const result = [];
        unique.forEach((value) => result.push(value));
        result.sort((n1, n2) => n1.node.id - n2.node.id);
        return result;
    }
    addAllSelectedChildren(unique) {
        if (this.selected && !unique.has(this.node.id)) {
            unique.set(this.node.id, this);
            for (const child of this.children) {
                child.addAllSelectedChildren(unique);
            }
        }
    }
}
class NexusModelRenderer {
    constructor(context) {
        this.viewpoint = new Array(4);
        this.planes = new Array(24);
        this.resolution = 1000;
        this.currentError = 1e20;
        this.gl = context.gl;
        this.targetError = context.settings.targetError;
        this.context = context;
    }
    get drawBudget() {
        return this.context.settings.drawBudget;
    }
    /**
     * Updates the viewpoint, planes and resolution on the context object
     */
    updateView(viewport, projection, modelView) {
        const modelViewProj = new Array(16);
        matrix_1.Matrix.matMul(projection, modelView, modelViewProj);
        this.planes = NexusModelRenderer.computePlanes(modelViewProj);
        this.viewpoint = NexusModelRenderer.computeViewpoint(modelView);
        const dist = NexusModelRenderer.computeDistance(this.viewpoint, modelViewProj);
        const side = NexusModelRenderer.computeSide(this.planes);
        this.resolution = 2 * side / (viewport[2] * dist); //2* brings it back to the value of the orig nexus.
    }
    static computeDistance(viewpoint, modelViewProj) {
        const modelViewProjInv = new Array(16);
        matrix_1.Matrix.matInv(modelViewProj, modelViewProjInv);
        const mi = modelViewProjInv;
        //center of the scene is M'*(0, 0, 0, 1)
        const rx = mi[3] / mi[15] - viewpoint[0];
        const ry = mi[7] / mi[15] - viewpoint[1];
        const rz = mi[11] / mi[15] - viewpoint[2];
        return Math.sqrt(rx * rx + ry * ry + rz * rz);
    }
    static computeSide(planes) {
        //side is M'(1,0,0,1) - M'(-1,0,01) and they lie on the planes
        const dx = planes[0] * planes[3] - planes[4] * planes[7];
        const dy = planes[1] * planes[3] - planes[5] * planes[7];
        const dz = planes[2] * planes[3] - planes[6] * planes[7];
        return Math.sqrt(dx * dx + dy * dy + dz * dz);
    }
    static computePlanes(modelViewProj) {
        const planes = new Array(24);
        const m = modelViewProj;
        //left, right, bottom, top as Ax + By + Cz + D = 0;
        planes[0] = m[0] + m[3];
        planes[1] = m[4] + m[7];
        planes[2] = m[8] + m[11];
        planes[3] = m[12] + m[15];
        planes[4] = -m[0] + m[3];
        planes[5] = -m[4] + m[7];
        planes[6] = -m[8] + m[11];
        planes[7] = -m[12] + m[15];
        planes[8] = m[1] + m[3];
        planes[9] = m[5] + m[7];
        planes[10] = m[9] + m[11];
        planes[11] = m[13] + m[15];
        planes[12] = -m[1] + m[3];
        planes[13] = -m[5] + m[7];
        planes[14] = -m[9] + m[11];
        planes[15] = -m[13] + m[15];
        for (let i = 0; i < 16; i += 4) {
            const l = Math.sqrt(planes[i] * planes[i] + planes[i + 1] * planes[i + 1] + planes[i + 2] * planes[i + 2]);
            planes[i] /= l;
            planes[i + 1] /= l;
            planes[i + 2] /= l;
            planes[i + 3] /= l;
        }
        return planes;
    }
    static computeViewpoint(modelView) {
        const modelViewInv = new Array(16);
        matrix_1.Matrix.matInv(modelView, modelViewInv);
        const viewpoint = new Array(4);
        viewpoint[0] = modelViewInv[12];
        viewpoint[1] = modelViewInv[13];
        viewpoint[2] = modelViewInv[14];
        viewpoint[3] = 1.0;
        return viewpoint;
    }
    /**
     * TODO: move to own class - nodes selector, add viewpoint and limits as parameters
     * 	methods can easily be static then
     */
    computeSelectedNodes(model) {
        if (debug_1.Debug.extract) {
            return [];
        }
        const visitQueue = new priority_queue_1.PriorityQueue(model.nodes.length);
        const rootNodes = NexusModelRenderer.computeTraversalNodesTree(model.nodes);
        for (const rootNode of rootNodes) {
            this.insertNode(visitQueue, rootNode);
        }
        this.targetError = this.context.state.currentError;
        this.currentError = 1e20;
        this.context.state.drawSize = 0;
        let nblocked = 0;
        let requested = 0;
        while (visitQueue.size && nblocked < this.context.settings.maxBlocked) {
            const error = visitQueue.error[0];
            const node = visitQueue.pop();
            const nodeIndex = node.node;
            if ((requested < this.context.settings.maxPending) && (nodeIndex.status === objects_1.NexusNodeDataStatus.ABSENT)) {
                this.context.candidates.push({ context: this.context, model: model, node: nodeIndex, frame: this.context.frame, error: error });
                requested++;
            }
            const blocked = node.blocked || !this.expandNode(node.node, error);
            if (blocked) {
                nblocked++;
            }
            else {
                node.selected = true;
                this.currentError = error;
                this.context.state.currentRenderError = this.currentError;
            }
            this.insertChildren(visitQueue, node, blocked);
        }
        this.context.state.nodesBlocked = nblocked;
        return NexusModelRenderer.computeSelectedNodes(rootNodes);
    }
    insertNode(visitQueue, traversalNode) {
        traversalNode.visited = true;
        const error = this.computeNodeErrorForCurrentViewpoint(traversalNode.node);
        if (traversalNode.node.id > 0 && error < this.targetError * 0.8) {
            return; //2% speed original TODO check if needed
        }
        // Update error for nodes in current frame.
        const node = traversalNode.node;
        if (node.renderState.frame != this.context.frame || node.renderState.error < error) {
            node.renderState.error = error;
            node.renderState.frame = this.context.frame;
        }
        visitQueue.push(traversalNode, error);
    }
    insertChildren(visitQueue, node, block) {
        for (const child of node.children) {
            if (child.node.sink) {
                return;
            }
            child.blocked = block || child.blocked;
            if (!child.visited) {
                this.insertNode(visitQueue, child);
            }
        }
    }
    expandNode(node, error) {
        if (node.id > 0 && error < this.targetError) { //error
            return false;
        }
        if (this.context.state.drawSize > this.drawBudget) {
            return false;
        }
        const sp = node.sphere;
        if (this.isVisibleInCurrentPlanes(sp.x, sp.y, sp.z, sp.radius)) { //expanded radius
            this.context.state.drawSize += node.vertices * 0.8;
        }
        //we are adding half of the new faces. (but we are using the vertices so *2)
        return node.status === objects_1.NexusNodeDataStatus.PRESENT; //not ready return false
    }
    computeNodeErrorForCurrentViewpoint(nodeIndex) {
        const sphere = nodeIndex.sphere;
        const xDiff = this.viewpoint[0] - sphere.x;
        const yDiff = this.viewpoint[1] - sphere.y;
        const zDiff = this.viewpoint[2] - sphere.z;
        let nodeToViewpointDistance = Math.sqrt(xDiff * xDiff + yDiff * yDiff + zDiff * zDiff) - sphere.radius;
        nodeToViewpointDistance = Math.max(nodeToViewpointDistance, 0.1);
        const visibilityCorrection = !this.isVisibleInCurrentPlanes(sphere.x, sphere.y, sphere.z, sphere.radius) ? 1000.0 : 1.0;
        return nodeIndex.error / (this.resolution * nodeToViewpointDistance) / visibilityCorrection;
    }
    isVisibleInCurrentPlanes(x, y, z, r) {
        const planes = this.planes;
        for (let planeOffset = 0; planeOffset < 16; planeOffset += 4) {
            if (planes[planeOffset] * x + planes[planeOffset + 1] * y + planes[planeOffset + 2] * z + planes[planeOffset + 3] + r < 0) { //ax+by+cz+w = 0;
                return false;
            }
        }
        return true;
    }
    /**
     * TODO: change to accept attributes and gl reference to remove instance values
     */
    renderNodes(model, selectedNodes) {
        const gl = this.gl;
        const attr = this.context.webGLRendererAttributes;
        const vertexEnabled = gl.getVertexAttrib(attr.position, gl.VERTEX_ATTRIB_ARRAY_ENABLED);
        const normalEnabled = attr.normal >= 0 ? gl.getVertexAttrib(attr.normal, gl.VERTEX_ATTRIB_ARRAY_ENABLED) : false;
        const colorEnabled = attr.color >= 0 ? gl.getVertexAttrib(attr.color, gl.VERTEX_ATTRIB_ARRAY_ENABLED) : false;
        const uvEnabled = attr.uv >= 0 ? gl.getVertexAttrib(attr.uv, gl.VERTEX_ATTRIB_ARRAY_ENABLED) : false;
        if (attr.color >= 0) {
            gl.vertexAttrib4fv(attr.color, [0.8, 0.8, 0.8, 1.0]);
        }
        if (debug_1.Debug.nodes) {
            gl.disableVertexAttribArray(attr.color);
            gl.disableVertexAttribArray(attr.uv);
        }
        let rendered = 0;
        let last_texture = new objects_1.NexusNodeTexture(-1);
        let drawnNodes = 0;
        for (const node of selectedNodes) {
            const nodeIndex = node.node;
            // If all children are to be drawn, skip the current node.
            if (model.mode !== 'POINT') {
                const nonSelectedChildNode = node.children.find(child => !child.selected);
                if (!nonSelectedChildNode) {
                    continue;
                }
            }
            const sp = nodeIndex.sphere;
            if (!this.isVisibleInCurrentPlanes(sp.x, sp.y, sp.z, sp.tightRadius)) { //tight radius
                continue;
            }
            drawnNodes++;
            gl.bindBuffer(gl.ARRAY_BUFFER, nodeIndex.mesh.verticesGlBuffer);
            if (model.mode != "POINT") {
                gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, nodeIndex.mesh.indicesGlBuffer);
            }
            gl.vertexAttribPointer(attr.position, 3, gl.FLOAT, false, 12, 0);
            gl.enableVertexAttribArray(attr.position);
            let offset = nodeIndex.vertices * 12;
            if (model.vertex.texCoord) {
                if (attr.uv >= 0) {
                    gl.vertexAttribPointer(attr.uv, 2, gl.FLOAT, false, 8, offset);
                    gl.enableVertexAttribArray(attr.uv);
                }
                offset += nodeIndex.vertices * 8;
            }
            if (model.vertex.normal) {
                if (attr.normal >= 0) {
                    gl.vertexAttribPointer(attr.normal, 3, gl.SHORT, true, 6, offset);
                    gl.enableVertexAttribArray(attr.normal);
                }
                offset += nodeIndex.vertices * 6;
            }
            if (model.vertex.color) {
                if (attr.color >= 0) {
                    gl.vertexAttribPointer(attr.color, 4, gl.UNSIGNED_BYTE, true, 4, offset);
                    gl.enableVertexAttribArray(attr.color);
                }
                offset += nodeIndex.vertices * 4;
            }
            if (debug_1.Debug.nodes) {
                gl.vertexAttrib4fv(attr.color, [(nodeIndex.id * 200 % 255) / 255.0, (nodeIndex.id * 140 % 255) / 255.0, (nodeIndex.id * 90 % 255) / 255.0, 1]);
            }
            if (debug_1.Debug.draw) {
                continue;
            }
            if (model.mode == "POINT") {
                const error = this.computeNodeErrorForCurrentViewpoint(nodeIndex); //because we lost it due to the instance problem
                let pointsize = Math.floor(0.30 * error);
                if (pointsize > 1) {
                    pointsize = 1;
                }
                // gl.vertexAttrib1fv(5, [pointsize]);
                let fraction = (error / this.currentError - 1);
                if (fraction > 1) {
                    fraction = 1;
                }
                const count = nodeIndex.vertices;
                if (count != 0) {
                    if (model.vertex.texCoord) {
                        const texture = nodeIndex.texture;
                        if (!!texture && texture !== last_texture) { //bind texture
                            gl.activeTexture(gl.TEXTURE0);
                            gl.bindTexture(gl.TEXTURE_2D, nodeIndex.texture.webGLId);
                        }
                    }
                    gl.drawArrays(gl.POINTS, 0, count);
                    rendered += count;
                }
                continue;
            }
            //concatenate renderings to remove useless calls. except we have textures.
            offset = 0;
            let end = 0;
            nodeIndex.patches.forEach((patch, index) => {
                // if (!t.selected[patch.node.id]) {
                if (!node.isChildSelected(patch.node.id)) {
                    end = patch.lastTriangle;
                    if (index < nodeIndex.patches.length - 1) {
                        return; // continue to next iteration
                    }
                }
                if (end > offset) {
                    if (model.hasTextures) {
                        const texture = patch.texture;
                        if (!!texture && texture !== last_texture) { //bind texture
                            gl.activeTexture(gl.TEXTURE0);
                            gl.bindTexture(gl.TEXTURE_2D, nodeIndex.texture.webGLId);
                            last_texture = texture;
                        }
                    }
                    gl.drawElements(gl.TRIANGLES, (end - offset) * 3, gl.UNSIGNED_SHORT, offset * 6);
                    rendered += end - offset;
                }
                offset = patch.lastTriangle;
            });
        }
        this.context.state.nodesRendered = drawnNodes;
        if (!vertexEnabled) {
            gl.disableVertexAttribArray(attr.position);
        }
        if (!normalEnabled && attr.normal >= 0) {
            gl.disableVertexAttribArray(attr.normal);
        }
        if (!colorEnabled && attr.color >= 0) {
            gl.disableVertexAttribArray(attr.color);
        }
        if (!uvEnabled && attr.uv >= 0) {
            gl.disableVertexAttribArray(attr.uv);
        }
        // gl.bindBuffer(gl.ARRAY_BUFFER, null);
        if (model.mode != "POINT") {
            // gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, null);
        }
    }
    // TODO: add viewpoint and planes as parameter, hold those in the context
    // 	 might just set the context to it?
    render(model) {
        const selectedNodes = this.computeSelectedNodes(model);
        this.renderNodes(model, selectedNodes);
    }
    static computeTraversalNodesTree(nodes) {
        const traversalNodes = new Map();
        for (const node of nodes) {
            traversalNodes.set(node.id, new NodeTraversalState(node));
        }
        for (const node of nodes) {
            const traversalNode = traversalNodes.get(node.id);
            if (!traversalNode) {
                throw 'could not construct traversal tree, unknown node id ' + node.id;
            }
            for (const patch of node.patches) {
                const childTraversalNode = traversalNodes.get(patch.node.id);
                if (!childTraversalNode) {
                    throw 'could not construct traversal tree, unknown child id ' + patch.node.id;
                }
                traversalNode.children.push(childTraversalNode);
            }
        }
        return Array.from(traversalNodes.values()).filter(traversalNode => traversalNode.node.root);
    }
    static computeSelectedNodes(roots) {
        const unique = new Map();
        for (const root of roots) {
            root.addAllSelectedChildren(unique);
        }
        const result = [];
        unique.forEach((value) => result.push(value));
        result.sort((n1, n2) => n1.node.id - n2.node.id);
        return result;
    }
}
exports.NexusModelRenderer = NexusModelRenderer;
